import React, { useCallback, useMemo } from 'react';
import { Button, DataGrid, DataGridColumn, Typography } from '../../design-system';
import {
  SwHeightMethod,
  SwHeightRockTypeParams,
  SwHeightZone
} from '../../@types/swHeight/types';
import { SECTION_TITLES } from './swHeightSettings';
import styles from './SwHeightRockTypeGrid.module.css';

/** The numeric cells, in display order, per methodology. */
const FIELDS: Record<
  SwHeightMethod,
  { key: keyof SwHeightRockTypeParams; header: string }[]
> = {
  brooks_corey: [
    { key: 'swirr', header: 'Swirr' },
    { key: 'pe', header: 'Pe (psi)' },
    { key: 'lambda_value', header: 'Lambda' }
  ],
  // Liao derives lambda from a, b and Pe, so it has no lambda cell.
  liao: [
    { key: 'swirr', header: 'Swirr' },
    { key: 'pe', header: 'Pe (psi)' },
    { key: 'best_a', header: 'a' },
    { key: 'best_b', header: 'b' },
    { key: 'true_pc_max', header: 'Pc max (psi)' }
  ]
};

export const EMPTY_OVERRIDE: SwHeightRockTypeParams = {
  swirr: null,
  pe: null,
  lambda_value: null,
  best_a: null,
  best_b: null,
  true_pc_max: null,
  source: 'manual'
};

type Row = {
  uid: string;
  rockType: number;
  range: string;
  edited: boolean;
} & Record<string, string | number | boolean | null>;

const sanitizeNumeric = (value: string) => value.replace(/[^0-9.,-]/g, '');

const parseNumber = (raw: string): number | null => {
  const trimmed = String(raw).trim().replace(',', '.');
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const format = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return String(Number(value.toFixed(6)));
};

export interface SwHeightRockTypeGridProps {
  zones: SwHeightZone[];
  method: SwHeightMethod;
  overrides: Record<string, SwHeightRockTypeParams>;
  onChange: (overrides: Record<string, SwHeightRockTypeParams>) => void;
  /** Render the group's own heading. Off when a drawer title carries it. */
  showHeader?: boolean;
  disabled?: boolean;
}

/**
 * One parameter set per rock type — what drives the second saturation
 * curve, `result.sw_by_rock_type`.
 *
 * Values come from the correlation's zone values; every cell is editable.
 * An edit is stored as an override keyed by zone uid, and clearing a cell
 * drops back to the correlation's own value rather than to zero — which is
 * why the empty string means "inherit" here, not "0".
 */
export const SwHeightRockTypeGrid: React.FC<SwHeightRockTypeGridProps> = ({
  zones,
  method,
  overrides,
  onChange,
  showHeader = true,
  disabled = false
}) => {
  const fields = FIELDS[method];

  // What each cell shows: the user's override when present, otherwise the
  // value the correlation resolved for this zone.
  const effective = useCallback(
    (zone: SwHeightZone, key: keyof SwHeightRockTypeParams) => {
      const override = overrides[zone.uid];
      const overridden = override?.[key];
      if (typeof overridden === 'number') return overridden;
      const resolved = zone.params?.[key];
      return typeof resolved === 'number' ? resolved : null;
    },
    [overrides]
  );

  const rows = useMemo<Row[]>(
    () =>
      zones.map((zone, index) => {
        const row: Row = {
          uid: zone.uid,
          rockType: index + 1,
          range: `${zone.start} – ${zone.end}`,
          edited: Boolean(overrides[zone.uid])
        };
        fields.forEach(field => {
          row[field.key] = format(effective(zone, field.key));
        });
        return row;
      }),
    [zones, fields, overrides, effective]
  );

  const saveRow = useCallback(
    (updated: Row) => {
      const zone = zones.find(candidate => candidate.uid === updated.uid);
      if (!zone) return;
      const next = { ...overrides };
      const override: SwHeightRockTypeParams = {
        ...EMPTY_OVERRIDE,
        ...next[zone.uid],
        source: 'manual'
      };
      let anySet = false;
      const cells = override as unknown as Record<string, number | null>;
      fields.forEach(field => {
        const value = parseNumber(String(updated[field.key] ?? ''));
        // Only record a genuine departure from the correlation's value, so
        // re-typing the inherited number does not create a phantom override.
        const inherited = zone.params?.[field.key];
        const differs =
          value !== null &&
          (typeof inherited !== 'number' || value !== inherited);
        cells[field.key] = differs ? value : null;
        anySet = anySet || differs;
      });
      if (anySet) {
        next[zone.uid] = override;
      } else {
        delete next[zone.uid];
      }
      onChange(next);
    },
    [zones, fields, overrides, onChange]
  );

  const resetRow = useCallback(
    (uid: string) => {
      const next = { ...overrides };
      delete next[uid];
      onChange(next);
    },
    [overrides, onChange]
  );

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        key: 'rockType',
        header: 'Rock Type',
        width: '90px',
        editable: false,
        getValue: row => `RT ${row.rockType}`
      },
      {
        key: 'range',
        header: 'FZI range',
        width: '120px',
        editable: false,
        getValue: row => row.range
      },
      ...fields.map<DataGridColumn<Row>>(field => ({
        key: field.key as string,
        header: field.header,
        align: 'right' as const,
        editable: !disabled,
        sanitizeInput: sanitizeNumeric,
        getValue: row => (row[field.key] as string) || '—'
      })),
      {
        key: 'source',
        header: 'Source',
        width: '110px',
        editable: false,
        getValue: row => (
          <span
            className={row.edited ? styles.badgeManual : styles.badgeFit}
            data-testid={`rock-type-source-${row.rockType}`}
          >
            {row.edited ? 'manual' : 'correlation'}
          </span>
        )
      },
      {
        key: 'reset',
        header: '',
        width: '90px',
        editable: false,
        getValue: row => (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled || !row.edited}
            data-testid={`rock-type-reset-${row.rockType}`}
            onClick={event => {
              event.stopPropagation();
              resetRow(row.uid);
            }}
          >
            Reset
          </Button>
        )
      }
    ],
    [fields, disabled, resetRow]
  );

  if (zones.length === 0) {
    return (
      <div className={styles.empty} data-testid="rock-type-grid-empty">
        <Typography.Text>
          This project has no rock-type zones, so parameters cannot be set per
          rock type. Define FZI ranges in the Rock Types workflow first.
        </Typography.Text>
      </div>
    );
  }

  const anyValues = zones.some(zone => zone.params);

  return (
    <div className={styles.wrapper} data-testid="rock-type-grid">
      <div className={styles.header}>
        {showHeader && (
          <Typography.Text weight="semibold">
            {SECTION_TITLES['rock-types']}
          </Typography.Text>
        )}
        <Typography.Text size="sm">
          Click a row to edit. Clear a cell to fall back to the correlation.
        </Typography.Text>
      </div>
      {!anyValues && (
        <Typography.Text size="sm" data-testid="rock-type-grid-no-values">
          The correlation has not produced values for any rock type yet. Run
          the correlation workflow, or enter the parameters by hand.
        </Typography.Text>
      )}
      {/*
       * minWidth is what makes the scroll area work. DataGrid's table is
       * `width: 100%; table-layout: fixed`, so without a floor it can never
       * exceed its container -- a narrow one squeezes the columns instead,
       * and the unsized editable ones collapse until their `nowrap` contents
       * overlap. Roughly the sum of the column widths: 90 + 120 + 110 + 90
       * fixed, plus ~110 per editable column.
       */}
      <DataGrid<Row>
        columns={columns}
        data={rows}
        rowKey="uid"
        editableRows={!disabled}
        striped
        minWidth={410 + fields.length * 110}
        onRowSave={saveRow}
        emptyMessage="No rock types."
      />
    </div>
  );
};

export default SwHeightRockTypeGrid;
